import groupsConfig from '@/config/groups';
import { PROPERTY_TYPE_DEFAULT, PROPERTY_TYPES } from '@/config/property-types.js';
import searchConfig from '@/config/search';

export function useSearch() {
  const runtimeConfig = useRuntimeConfig();
  const envData = runtimeConfig?.public;

  // ### STATIC VALUES
  let destinationInputTimer = null;
  const destinationInputDebounce = 750;
  const searchMaxResultsAllowed = 70;
  const recentSearchesStorageKey = 'recentSearches';

  // ### STORES
  const languageStore = useLanguageStore();
  const searchStore = useSearchStore();
  const routeStore = useRouteStore();
  const testingStore = useTestingStore();

  // ### FEATURE TOGGLES
  const newAutoCompleteServiceEnabled = testingStore.hasEnabledFeature('web_newAutoCompleteService');
  const geoSpatialBaliEnabled = testingStore.hasEnabledFeature('web_geoBaliPoC');
  const geoSpatialSearchEnabled = testingStore.hasEnabledFeature('web_geoSpatialSearch');
  const geoSpatialSearchVars = geoSpatialSearchEnabled
    ? testingStore.getFeatureVariables('web_geoSpatialSearch')
    : { regions_enabled: { regions: [] } };

  // ### DATA
  const searchResults = ref([]);
  const loadedMoreSearchResults = ref(false);
  const loadingMoreSearchResults = ref(false);
  const wizardActiveStep = ref('destination');
  const wizardIsOpen = ref(false);
  const lastSearchedTerm = ref('');
  const userRecentSearches = useCookie(recentSearchesStorageKey, {
    default: () => [],
    domain: envData?.COOKIE_DOMAIN,
    path: '/',
    sameSite: 'Strict',
    secure: true,
    watch: true,
  });

  // ### COMPOSABLES
  const { t } = useI18n();
  const { $tracking } = useNuxtApp();
  const dateTime = useDateTime();
  const { isBelowMediumScreen } = useMq();
  const { searchForTerm } = useApiAutoComplete();
  const { getNearestCityByCoordinates } = useApiCities();
  const { getContinentById } = useApiContinents();
  const { getCountryById } = useApiCountries();
  const { getAreaById } = useApiAreas();
  const { getRegionById } = useApiRegions();
  const { useUrl } = useUrls();
  const { useFormatter } = useFormatters();

  // ### HELPER FUNCTIONS
  const isValidRecentSearch = function (us) {
    return Boolean(us?.destination?.label)
      && dateTime.isValidDate(us?.checkin)
      && dateTime.isValidDate(us?.createdOn)
      && typeof us?.nights === 'number'
      && us.nights > 0;
  };

  // ### COMPUTED
  const getGuestsGroupTypes = computed(() => {
    return groupsConfig.groupTypes.map((gtype) => ({
      ...gtype,
      label: t(gtype.label),
      value: String(gtype.value),
    }));
  });

  const getSearchFormTranslations = computed(() => ({
    tAgeRanges: t('t_RWDAGERANGES'),
    tApplyDates: t('t_FORMINPUTAPPLY'),
    tCheckin: t('t_FORMINPUTCHECKIN'),
    tCheckout: t('t_FORMINPUTCHECKOUT'),
    tClearDates: t('t_FORMINPUTCLEAR'),
    tCurrentLocation: t('t_FORMINPUTCURRENTLOCATION'),
    tDates: t('t_FORMINPUTDATES'),
    tDatesNoCheckInError: t('t_FORMINPUTSELECTCHECKIN'),
    tDatesNoCheckOutError: t('t_FORMINPUTSELECTCHECKOUT'),
    tDestination: t('t_FORMINPUTDESTINATION'),
    tDestinationCurrentLocationError: t('t_FORMERRORNOCURRENTLOCATION'),
    tDestinationEmptyResultsError: t('t_FORMERROREMPTYRESULTS'),
    tDestinationFieldLabel: t('t_FORMINPUTWHEREDOYOUWANTTOGO'),
    tDestinationResultsViewMore: t('t_SEARCHRESULTSSEEMORE'),
    tGroupType: t('t_RWDGROUPTYPE'),
    tGuests: t('t_FORMINPUTGUESTS'),
    tGuestsNoAgeRangeError: t('t_NOAGERANGEERROR'),
    tGuestsNoGroupTypeError: t('t_NOGROUPTYPEERROR'),
    tMsgNumNightsSelected: t('t_FORMINFOXNIGHTSSELECTED', { NUMNIGHTS: '##NUMNIGHTS##' }),
    tMsgSelectCheckInDate: t('t_FORMINPUTSELECTCHECKIN'),
    tMsgSelectCheckOutDate: t('t_FORMINPUTSELECTCHECKOUT'),
    tNextButton: t('t_FORMINPUTNEXT'),
    tSearchResultsAria: t('t_SEARCHRESULTSARIA'),
    tSubmitLetsGo: t('t_FORMINPUTLETSGO'),
  }));

  const getUserRecentSearchesForForm = computed(() => {
    if (!userRecentSearches.value?.length) {
      return null;
    }

    return [
      {
        label: t('t_SEARCHFORMRECENTSEARCHES'),
        items: userRecentSearches.value
          ?.filter((rs) => isValidRecentSearch(rs))
          ?.sort((rsA, rsB) => new Date(rsB.createdOn) - new Date(rsA.createdOn))
          ?.map((rs) => {
            const guests = rs?.guests ?? { guestsQty: 1 };
            const guestsTranscode = guests.guestsQty > 1 ? 't_RWDGUESTS' : 't_RWDGUEST';

            const checkinObj = dateTime.formatFromFlatToObject(rs.checkin);
            const checkoutObj = dateTime.addDaysToDate(checkinObj, rs.nights);
            const checkinLabel = dateTime.formatToHumanReadableShortDate(
              checkinObj,
              languageStore.getCurrentLanguage.language,
            );
            const checkoutLabel = dateTime.formatToHumanReadableShortDate(
              checkoutObj,
              languageStore.getCurrentLanguage.language,
            );

            return {
              ...rs.destination,
              value: rs.destination.id,
              subLabel: `${checkinLabel} - ${checkoutLabel} ∙ ${guests.guestsQty} ${t(guestsTranscode)}`,
            };
          }),
      },
    ];
  });

  const getSearchFormCommonParams = computed(() => ({
    lang: languageStore.getCurrentLanguage.lang,
    destinationDefault: searchStore.getSearchLocation,
    destinationSearchResults: searchResults.value,
    destinationMinCharsForSearch: 2,
    destinationMoreButton: {
      enabled: newAutoCompleteServiceEnabled && searchResults.value.length > 0 && !loadedMoreSearchResults.value,
      loading: loadingMoreSearchResults.value,
    },
    dateCheckIn: searchStore.getSearchCheckIn,
    dateCheckOut: searchStore.getSearchCheckOut,
    maxNightsOfStay: searchConfig.extendedMaxNights,
    guestsQty: searchStore.getSearchNumberOfGuests,
    guestsMax: searchConfig.maxGuests,
    groupThreshold: groupsConfig.minGuests - 1,
    groupTypes: getGuestsGroupTypes.value,
    selectedGroupType:
      searchStore.getSearchGroupType?.value != null && searchStore.getSearchGroupType?.value !== ''
        ? String(searchStore.getSearchGroupType.value)
        : undefined,
    selectedGroupAges: searchStore.getSearchGroupAge,
    groupAgeRanges: groupsConfig.ageRanges,
    translations: getSearchFormTranslations.value,
  }));

  const geoSpatialValidRegions = computed(() => {
    const regions = geoSpatialSearchVars?.regions_enabled?.regions;
    if (!Array.isArray(regions)) {
      return [];
    }
    return regions.map((region) => region.id);
  });

  // ### METHODS

  /**
   * Drops recent searches that are invalid or expired:
   * - Invalid: check-in is not a valid date, destination label is missing,
   *            or nights is not a positive number
   * - Expired: check-in is in the past or saved more than
   *            `recentSearchesExpirationDays` ago
   * Only writes to the cookie when at least one entry actually changes.
   */
  const removeInvalidOrExpiredRecentSearches = function () {
    const current = userRecentSearches.value ?? [];
    if (!current.length) {
      return;
    }

    const upToDate = current.filter((us) => {
      if (!isValidRecentSearch(us)) {
        return false;
      }

      const daysUntilCheckin = dateTime.getDiffInDays(dateTime.today, us.checkin);
      const daysSinceSaved = dateTime.getDiffInDays(us.createdOn, dateTime.today);
      return daysUntilCheckin >= 0 && daysSinceSaved <= searchConfig.recentSearchesExpirationDays;
    });

    if (upToDate.length !== current.length) {
      userRecentSearches.value = upToDate;
    }
  };

  const isGeoSpatialValidRegion = function (regionId) {
    const validRegions = [];
    if (geoSpatialBaliEnabled) {
      validRegions.push(52);
    }
    if (geoSpatialSearchEnabled) {
      validRegions.push(...geoSpatialValidRegions.value);
    }
    return validRegions.includes(regionId);
  };

  const getSearchResultCategoryName = function (category) {
    switch (category) {
      case 'CONTINENT':
        return t('t_SEARCHCATEGORYCONTINENT');
      case 'COUNTRY':
        return t('t_SEARCHCATEGORYCOUNTRY');
      case 'STATE':
      case 'REGION':
        return t('t_SEARCHCATEGORYREGION');
      case 'CITY':
        return t('t_SEARCHCATEGORYCITY');
      case 'CITYDISTRICTS':
        return t('t_SEARCHCATEGORYDISTRICT');
      case 'PROPERTY':
        return t('t_SEARCHCATEGORYPROPERTY');
      default:
        return '';
    }
  };

  const getSearchResultIcon = function (type) {
    switch (type) {
      case 'COUNTRY':
        return 'globe';
      case 'CITY':
        return 'city';
      case 'CITYDISTRICTS':
        return 'landmark';
      case 'PROPERTY':
        return 'hostel';
      default:
        return 'location-pin';
    }
  };

  const handleSearchResults = function (searchResponse, searchMaxResults = null, groupResults = true) {
    if (!Array.isArray(searchResponse) || (searchResponse?.length === 1 && searchResponse[0]?.type === 'no-results')) {
      return [];
    }

    const maxResults = searchMaxResults || searchMaxResultsAllowed;

    return searchResponse?.slice(0, maxResults)?.reduce((prevState, result, resultIndex) => {
      const searchOptionType = result?.type === 'popular places' ? 'citydistricts' : result?.type;

      const searchOption = {
        id: result?.id,
        type: searchOptionType,
        category: searchOptionType.toUpperCase(),
      };

      searchOption.labelContent = result.name;
      searchOption.cityName = result?.city?.name || null;
      searchOption.cityId = result?.city?.id || null;

      if (!searchOption.labelContent) {
        return prevState;
      }

      const [label, ...labelSmall] = searchOption.labelContent?.split(',');

      const resultItem = {
        id: searchOption.id,
        type: searchOptionType,
        city: searchOption?.cityName,
        cityId: searchOption?.cityId,
        label: label.trim(),
        labelSmall: labelSmall.join(',').trim(),
        labelSlug: useFormatter.normalizeUrl(label),
        value: String(searchOption.id),
        icon: getSearchResultIcon(searchOption.category),
        rank: resultIndex + 1,
      };

      if (groupResults) {
        const categoryName = getSearchResultCategoryName(searchOption.category);
        let categoryIndex = prevState.findIndex((c) => c.label === categoryName);
        if (categoryIndex < 0) {
          prevState.push({
            label: categoryName,
            items: [],
          });
          categoryIndex = prevState.length - 1;
        }
        prevState[categoryIndex].items.push(resultItem);
      } else {
        prevState.push(resultItem);
      }

      return prevState;
    }, []);
  };

  const handleDestinationFieldClicked = function () {
    wizardActiveStep.value = 'destination';
    wizardIsOpen.value = toValue(isBelowMediumScreen?.value);

    if (!searchStore.getIsSearchActivated) {
      $tracking?.onSearchActivated('destination');
      searchStore.setSearchActivated(true);

      if (userRecentSearches.value?.length) {
        $tracking?.onRecentDestinationsShown();
      }
    }
  };

  const handleDestinationSearch = async function (search) {
    // Validate and sanitize input
    const term = String(search.term || '').trim();

    // Max length check (adjust based on UI/backend requirements)
    if (term.length > 100) {
      console.warn('### Search term too long, truncating');
      lastSearchedTerm.value = term.substring(0, 100);
    } else {
      lastSearchedTerm.value = term;
    }

    clearTimeout(destinationInputTimer);
    loadedMoreSearchResults.value = false;
    loadingMoreSearchResults.value = false;

    destinationInputTimer = setTimeout(async () => {
      try {
        const searchResponse = await searchForTerm(
          lastSearchedTerm.value,
          newAutoCompleteServiceEnabled ? 'variation' : 'control',
        );
        searchResults.value = handleSearchResults(
          searchResponse,
          newAutoCompleteServiceEnabled ? 5 : null,
          !newAutoCompleteServiceEnabled,
        );
        $tracking?.onAutoCompleteSearchCall(lastSearchedTerm.value, searchResponse);
      } catch (error) {
        console.error('### Search failed:', error);
      }
    }, destinationInputDebounce);
  };

  const handleDestinationItemSelected = function (item) {
    $tracking?.onAutoCompleteSearchSelect(lastSearchedTerm.value, item);
    searchStore.setSearchParam('location', { ...item });
    searchStore.setSearchParam('phrase', `${item?.label}${item.labelSmall ? `, ${item.labelSmall}` : ''}`);
  };

  const handleDestinationMoreItemsSelected = async function () {
    // Ignore if already loading more results
    if (loadingMoreSearchResults.value || lastSearchedTerm.value === '') {
      return;
    }

    const trackingInfo = {
      event_name: 'Search Event',
      action: 'See More Clicked',
      autocomplete_term: lastSearchedTerm.value || '',
      autocomplete_search_results: searchResults?.value?.length || 0,
    };

    loadingMoreSearchResults.value = true;

    try {
      const extraSearchResponse = await searchForTerm(lastSearchedTerm.value, 'control');
      searchResults.value.push({ separator: true });
      searchResults.value.push({ label: t('t_SEARCHRESULTSALLRESULTS') });
      searchResults.value.push(...handleSearchResults(extraSearchResponse, null, true));

      loadedMoreSearchResults.value = true;

      $tracking?.onTrackEvent({
        ...trackingInfo,
        see_more_search_results: trackingInfo.autocomplete_search_results + (extraSearchResponse?.length || 0),
      });
    } catch (error) {
      console.error('### Failed to load more search results: ', error);
    } finally {
      loadingMoreSearchResults.value = false;
    }
  };

  const buildSearchRouteAndTrackingData = async function (params) {
    let searchUrl = null;
    const trackingObject = {};

    const propertyTypeObj
      = PROPERTY_TYPES.find((pt) => t(pt.slug) === routeStore.getPropertyType) || PROPERTY_TYPE_DEFAULT;

    const extraParams = {
      from: dateTime.formatToFlat(params.checkin),
      to: dateTime.formatToFlat(params.checkout),
      guests: params.guests.guestsQty,
    };

    let geoSpatialRegionId = null;

    switch (params.destination.type) {
      case 'continent': {
        const continentInfo = await getContinentById(propertyTypeObj.key, params.destination?.id);
        const continentData = {
          propertyTypeSlug: t(propertyTypeObj.slug),
          urlFriendlyContinent:
            continentInfo?.urlFriendlyName
            || params.destination?.labelSlug
            || useFormatter.normalizeUrl(params.destination?.label),
        };
        trackingObject.destination_continent = continentData?.urlFriendlyContinent;
        trackingObject.search_submitted_type = 'continent';

        searchUrl = useUrl.getContinentPageUrl(continentData, {});
        break;
      }
      case 'country': {
        const countryInfo = await getCountryById(propertyTypeObj.key, params.destination?.id);
        const countryData = {
          propertyTypeSlug: t(propertyTypeObj.slug),
          urlFriendlyContinent: countryInfo.urlFriendlyContinent,
          urlFriendlyCountry: countryInfo.urlFriendlyName,
        };
        trackingObject.destination_country = countryData?.urlFriendlyCountry;
        trackingObject.destination_continent = countryData?.urlFriendlyContinent;
        trackingObject.search_submitted_type = 'country';
        searchUrl = useUrl.getCountryPageUrl(countryData, {});
        break;
      }
      case 'state': {
        const areaInfo = await getAreaById(propertyTypeObj.key, params.destination?.id);
        const areaData = {
          propertyTypeSlug: t(propertyTypeObj.slug),
          urlFriendlyContinent: areaInfo?.urlFriendlyContinent,
          urlFriendlyCountry: areaInfo?.urlFriendlyCountry,
          urlFriendlyArea: areaInfo?.urlFriendlyName,
        };
        trackingObject.destination_area = areaData?.urlFriendlyArea;
        trackingObject.destination_country = areaData?.urlFriendlyCountry;
        trackingObject.destination_continent = areaData?.urlFriendlyContinent;
        trackingObject.search_submitted_type = 'area';
        searchUrl = useUrl.getAreaPageUrl(areaData, {});
        break;
      }
      case 'region': {
        const regionInfo = await getRegionById(propertyTypeObj.key, params.destination?.id);
        const regionData = {
          propertyTypeSlug: t(propertyTypeObj.slug),
          urlFriendlyContinent: regionInfo?.urlFriendlyContinent,
          urlFriendlyCountry: regionInfo?.urlFriendlyCountry,
          urlFriendlyRegion: regionInfo?.urlFriendlyName,
        };
        trackingObject.destination_region = regionData?.urlFriendlyRegion;
        trackingObject.destination_country = regionData?.urlFriendlyCountry;
        trackingObject.destination_continent = regionData?.urlFriendlyContinent;
        trackingObject.search_submitted_type = 'region';
        searchUrl = useUrl.getRegionPageUrl(regionData, {});

        if (isGeoSpatialValidRegion(regionInfo?.id)) {
          geoSpatialRegionId = regionInfo?.id;
          const regionLocationData = {
            name: regionInfo?.name,
            countryName: regionInfo?.country,
            id: regionInfo?.id,
            urlFriendlyName: regionInfo?.urlFriendlyName,
            urlFriendlyCountry: regionInfo?.urlFriendlyCountry,
            dateCheckIn: extraParams.from,
            dateCheckOut: extraParams.to,
            numOfGuests: extraParams.guests,
          };
          searchUrl = useUrl.getRegionPageUrlDynamic(regionLocationData, {});
        }
        break;
      }
      case 'city': {
        const searchString = [params.destination.label];

        if (params.destination.labelSmall) {
          searchString.push(params.destination.labelSmall);
        }

        const cityLocationData = {
          searchString: searchString.join(', '),
          cityId: params.destination.id,
          cityName: params.destination.label,
          countryName: params.destination.labelSmall,
          dateCheckIn: extraParams.from,
          dateCheckOut: extraParams.to,
          numOfGuests: extraParams.guests,
        };

        trackingObject.destination_city = params.destination?.label;
        trackingObject.destination_city_id = params.destination?.id?.toString();
        trackingObject.destination_country = params.destination?.labelSmall;
        trackingObject.auto_sort_order_applied = 'sfab';
        trackingObject.page_sort_order_applied = 'sfab';
        trackingObject.listing_type = 'list';
        trackingObject.search_submitted_type = 'city';

        searchUrl = useUrl.getCityPageUrlDynamic(cityLocationData);
        break;
      }
      case 'citydistricts': {
        const cityDistrictParts = params.destination?.labelSmall.split(',') || params.destination?.label?.split(',');

        const cityDistrictData = {
          searchString: params.destination.labelSmall,
          cityId: params.destination?.cityId,
          cityName: cityDistrictParts?.[0]?.trim(),
          countryName: cityDistrictParts?.[1]?.trim(),
          dateCheckIn: extraParams.from,
          dateCheckOut: extraParams.to,
          numOfGuests: extraParams.guests,
        };

        trackingObject.destination_district = params.destination.label;
        trackingObject.destination_city = cityDistrictParts[0];
        trackingObject.destination_city_id = params.destination.cityId?.toString();
        trackingObject.destination_country = cityDistrictParts[1];
        trackingObject.auto_sort_order_applied = 'sfab';
        trackingObject.page_sort_order_applied = 'sfab';
        trackingObject.listing_type = 'list';
        trackingObject.search_submitted_type = 'district';

        searchUrl = useUrl.getCityPageUrlDynamic(cityDistrictData, { district: params.destination?.id });
        break;
      }
      case 'property': {
        const searchDestination = toValue(params.destination);
        const propertySearchName = searchDestination?.labelSlug || searchDestination?.label || '-';
        const propertySearchCity = searchDestination?.city || searchDestination?.labelSmall?.split(',')?.[0] || '-';
        const propertySearchId = searchDestination?.id > 0 ? searchDestination?.id : searchDestination?.value;
        const propertyLocationData = {
          propertyId: propertySearchId,
          urlFriendlyCity: useFormatter.normalizeUrl(propertySearchCity),
          urlFriendlyProperty: useFormatter.normalizeUrl(propertySearchName),
        };
        const propertyLocationQuery = {
          from: extraParams.from,
          to: extraParams.to,
          guests: extraParams.guests,
        };

        trackingObject.property_name = propertyLocationData?.urlFriendlyProperty;
        trackingObject.destination_city = propertyLocationData?.urlFriendlyCity;
        trackingObject.product_id = propertySearchId?.toString();
        trackingObject.sku = propertySearchId?.toString();
        trackingObject.search_submitted_type = 'property';

        searchUrl = useUrl.getPropertyPageUrlDynamic(propertyLocationData, propertyLocationQuery);
        break;
      }
      default:
        return;
    }

    if (
      params.guests.guestsQty >= groupsConfig.minGuests - 1
      && (['city', 'citydistricts', 'property'].includes(params.destination.type)
        || (['region'].includes(params.destination.type) && geoSpatialRegionId > 0))
    ) {
      const groupType = params.guests.groupType;
      const groupAges = params.guests.groupAges;

      if (groupType) {
        searchUrl += `&groupType=${groupType}`;
        trackingObject.group_type = groupsConfig.groupTypes.find((gt) => gt.value === Number.parseInt(groupType)).code;
      }

      if (groupAges.length > 0) {
        groupAges.forEach((groupAge) => {
          searchUrl += `&groupAgeRange=${groupAge}`;
        });
        trackingObject.group_age_range = groupAges
          .map((groupAge) => {
            const ageRangeObj = groupsConfig.ageRanges.find((ar) => ar.value === groupAge);
            return ageRangeObj?.label || null;
          })
          .join(',');
      }
    }

    if (routeStore.isPropertyPage && ['city', 'citydistricts', 'property'].includes(params.destination.type)) {
      searchUrl += '&origin=microsite';
    }

    const dateLeadTime = dateTime.getLeadTime(extraParams.from);

    trackingObject.arrival_date = extraParams.from;
    trackingObject.departure_date = extraParams.to;
    trackingObject.number_nights = dateTime.getDiffInDays(extraParams.from, extraParams.to);
    trackingObject.number_guests = extraParams.guests;
    trackingObject.property_type = routeStore.getPropertyType;
    trackingObject.lead_time = dateLeadTime > 0 ? dateLeadTime : 'zero';
    trackingObject.category = propertyTypeObj.key;
    trackingObject.search_keywords = params.destination?.labelSlug;
    // @TODO: COD-67 - Remove after A/B test
    trackingObject.geospatial_cohort = geoSpatialSearchEnabled ? 'variation' : 'control';
    trackingObject.search_label = `${params?.destination?.label}${params?.destination?.labelSmall ? `, ${params?.destination?.labelSmall}` : ''}`;

    return {
      searchUrl,
      trackingObject,
    };
  };

  const handleRecentDestinationItemSelected = async function (destination) {
    $tracking?.onRecentDestinationClicked();

    const existingDestination = toRaw(userRecentSearches.value).find((search) => {
      return (
        JSON.stringify(search.destination.id) === JSON.stringify(destination.id)
        && JSON.stringify(search.destination.type) === JSON.stringify(destination.type)
      );
    });

    if (!existingDestination) {
      return;
    }

    const checkinObj = dateTime.formatFromFlatToObject(existingDestination.checkin);
    const checkoutObj = dateTime.addDaysToDate(checkinObj, existingDestination.nights);

    const paramsToBuildRoute = {
      destination: existingDestination.destination,
      checkin: checkinObj,
      checkout: checkoutObj,
      guests: existingDestination.guests,
    };

    const searchRoute = await buildSearchRouteAndTrackingData(paramsToBuildRoute);

    setTimeout(() => {
      navigateTo(searchRoute.searchUrl, { external: true });
    }, 100);
  };

  const handleCurrentLocation = async function (position) {
    wizardActiveStep.value = 'destination';
    if (position.error) {
      return;
    }

    const latitude = position?.latitude || 0;
    const longitude = position?.longitude || 0;

    const nearestCity = await getNearestCityByCoordinates(latitude, longitude);

    if (nearestCity?.id) {
      handleDestinationItemSelected({
        id: nearestCity?.id,
        type: 'city',
        label: nearestCity?.name,
        labelSmall: nearestCity?.country,
        value: String(nearestCity?.id),
      });
    }
    wizardActiveStep.value = 'dates';
  };

  const handleDatesFieldClicked = function () {
    wizardActiveStep.value = 'dates';
    wizardIsOpen.value = toValue(isBelowMediumScreen?.value);

    if (!searchStore.getIsSearchActivated) {
      $tracking?.onSearchActivated('check-in date');
      searchStore.setSearchActivated(true);
    }
  };

  const handleDatesSelected = function (params) {
    searchStore.setSearchCheckIn(params?.checkin);
    searchStore.setSearchCheckOut(params?.checkout);
  };

  const handleGuestsFieldClicked = function () {
    wizardActiveStep.value = 'guests';
    wizardIsOpen.value = toValue(isBelowMediumScreen?.value);

    if (!searchStore.getIsSearchActivated) {
      $tracking?.onSearchActivated('guests');
      searchStore.setSearchActivated(true);
    }
  };

  const handleGuestsNumberChanged = function (numGuests) {
    searchStore.setSearchParam('guests', numGuests);
  };

  const handleGuestsGroupTypeChanged = function (groupType) {
    const groupTypeObject = getGuestsGroupTypes.value.find((gtype) => gtype.value === groupType);
    searchStore.setSearchParam('groupType', groupTypeObject);
  };

  const handleGuestsGroupAgesChanged = function (groupAges) {
    searchStore.setSearchParam('groupAge', groupAges);
  };

  const handleWizardClose = function () {
    wizardIsOpen.value = false;
  };

  const handleWizardOpen = function () {
    wizardActiveStep.value = 'destination';
    wizardIsOpen.value = true;

    if (!searchStore.getIsSearchActivated) {
      $tracking?.onSearchActivated(wizardActiveStep.value);
      searchStore.setSearchActivated(true);

      if (userRecentSearches.value?.length) {
        $tracking?.onRecentDestinationsShown();
      }
    }
  };

  const handleSaveSearchInStorage = function (params) {
    const checkinLabel = dateTime.formatToFlat(params.checkin);
    const searchId = params?.destination?.id > 0 ? params?.destination?.id : params?.destination?.value;
    const newUserRecentSearchEntry = {
      checkin: checkinLabel,
      nights: dateTime.getDiffInDays(params?.checkin, params?.checkout),
      guests: params?.guests,
      destination: {
        id: searchId,
        label: params.destination?.label,
        labelSmall: params.destination?.labelSmall,
        icon: params.destination?.icon ?? 'location-pin',
        type: params.destination?.type,
      },
      createdOn: new Date(),
    };

    if (params?.destination?.type === 'citydistricts') {
      newUserRecentSearchEntry.destination.cityId = params.destination.cityId;
    }

    const currentRecentSearches = userRecentSearches.value ?? [];

    // Find if the destination is already in the recent searches
    const destinationRecentSearchIndex = currentRecentSearches.findIndex(
      (obj) =>
        obj?.destination?.label === params?.destination?.label
        && obj?.destination?.labelSmall === params?.destination?.labelSmall,
    );

    const newRecentSearches = [...currentRecentSearches];

    if (destinationRecentSearchIndex < 0) {
      // If the destination is not in the recent searches add it.
      if (newRecentSearches.length >= searchConfig.maxRecentSearchesShown) {
        // If the recent searches are full, remove the oldest search and add the new one
        newRecentSearches.sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
        newRecentSearches.shift();
      }
      newRecentSearches.push(newUserRecentSearchEntry);
    } else {
      // If the destination is in the recent searches, replace it with the new one
      newRecentSearches.splice(destinationRecentSearchIndex, 1);
      newRecentSearches.unshift(newUserRecentSearchEntry);
    }

    userRecentSearches.value = newRecentSearches;
  };

  const handleSubmit = async function (params) {
    searchStore.setSearchCheckIn(params.checkin);
    searchStore.setSearchCheckOut(params.checkout);

    const searchUrlAndTrackingData = await buildSearchRouteAndTrackingData(params);

    const redirectUrl = searchUrlAndTrackingData.searchUrl;
    const trackingData = searchUrlAndTrackingData.trackingObject;

    if (redirectUrl) {
      // ### SAVE RECENT SEARCH TO STORAGE
      handleSaveSearchInStorage(params);

      $tracking?.onSearchSubmitted(trackingData);

      setTimeout(() => {
        navigateTo(redirectUrl, { external: true });
      }, 100);
    }
  };

  if (true) {
    removeInvalidOrExpiredRecentSearches();
  }

  return {
    wizardIsOpen,
    wizardActiveStep,
    getGuestsGroupTypes,
    getSearchFormTranslations,
    getSearchFormCommonParams,
    getUserRecentSearchesForForm,
    isGeoSpatialValidRegion,
    handleSearchResults,
    handleDestinationFieldClicked,
    handleDestinationSearch,
    handleDestinationItemSelected,
    handleDestinationMoreItemsSelected,
    handleRecentDestinationItemSelected,
    handleCurrentLocation,
    handleDatesFieldClicked,
    handleDatesSelected,
    handleGuestsFieldClicked,
    handleGuestsNumberChanged,
    handleGuestsGroupTypeChanged,
    handleGuestsGroupAgesChanged,
    handleWizardClose,
    handleWizardOpen,
    handleSubmit,
  };
}
